import React from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertCircle, BarChart3, Building2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePortfolioContracts } from '@/hooks/usePortfolioContracts';

export type EscopoType = 'contrato' | 'portfolio';

interface Props {
  escopo: EscopoType;
  onEscopoChange: (v: EscopoType) => void;
  clusterId: string | null;
  onClusterChange: (id: string) => void;
  contratoIds: string[];
  onContratoIdsChange: (ids: string[]) => void;
  /** Passa o nome do contrato "âncora" para exibir no modo contrato */
  contratoAncoraNome?: string;
  isGestorOrAbove: boolean;
}

const MAX_CONTRATOS = 20;

export const PortfolioScopeSelector: React.FC<Props> = ({
  escopo,
  onEscopoChange,
  clusterId,
  onClusterChange,
  contratoIds,
  onContratoIdsChange,
  contratoAncoraNome,
  isGestorOrAbove,
}) => {
  const { clusters, contratos, clustersLoading, contratosLoading } =
    usePortfolioContracts(escopo === 'portfolio' ? clusterId : null);

  const toggleContrato = (id: string) => {
    if (contratoIds.includes(id)) {
      onContratoIdsChange(contratoIds.filter(x => x !== id));
    } else if (contratoIds.length < MAX_CONTRATOS) {
      onContratoIdsChange([...contratoIds, id]);
    }
  };

  return (
    <div className="space-y-3">
      {/* ── Selector de escopo ──────────────────────────────────────── */}
      <Label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        Escopo do relatório
      </Label>

      <RadioGroup
        value={escopo}
        onValueChange={(v) => onEscopoChange(v as EscopoType)}
        className="flex gap-4"
      >
        <label className={`flex items-center gap-2 cursor-pointer rounded-lg border px-3 py-2 transition-colors flex-1 ${
          escopo === 'contrato' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
        }`}>
          <RadioGroupItem value="contrato" id="escopo-contrato" />
          <div className="flex items-center gap-1.5">
            <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
            <div>
              <p className="text-xs font-medium">Contrato único</p>
              {contratoAncoraNome && (
                <p className="text-xs text-muted-foreground truncate max-w-[120px]">{contratoAncoraNome}</p>
              )}
            </div>
          </div>
        </label>

        <label className={`flex items-center gap-2 cursor-pointer rounded-lg border px-3 py-2 transition-colors flex-1 ${
          !isGestorOrAbove ? 'opacity-40 cursor-not-allowed' : ''
        } ${escopo === 'portfolio' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}>
          <RadioGroupItem value="portfolio" id="escopo-portfolio" disabled={!isGestorOrAbove} />
          <div className="flex items-center gap-1.5">
            <BarChart3 className="h-3.5 w-3.5 text-muted-foreground" />
            <div>
              <p className="text-xs font-medium">Portfólio</p>
              <p className="text-xs text-muted-foreground">multi-contrato</p>
            </div>
          </div>
        </label>
      </RadioGroup>

      {/* ── Mensagem: perfil sem acesso a portfólio ──────────────────── */}
      {!isGestorOrAbove && (
        <div className="flex items-center gap-2 rounded-md bg-muted/50 border border-border px-3 py-2 text-xs text-muted-foreground">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          Portfólio disponível apenas para Gestor, Co-Admin e Admin.
        </div>
      )}

      {/* ── Configuração de portfólio ─────────────────────────────────── */}
      {escopo === 'portfolio' && isGestorOrAbove && (
        <div className="space-y-3 pt-1">
          {/* Cluster */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Cluster *</Label>
            {clustersLoading ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <Select value={clusterId ?? ''} onValueChange={(v) => { onClusterChange(v); onContratoIdsChange([]); }}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Selecione um cluster…" />
                </SelectTrigger>
                <SelectContent>
                  {clusters.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="flex items-center gap-2">
                          <span
                            className="inline-block h-2 w-2 rounded-full shrink-0 border border-border/50"
                            style={{ backgroundColor: c.color }}
                          />
                        {c.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Contratos */}
          {clusterId && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-muted-foreground">Contratos *</Label>
                <Badge
                  variant={contratoIds.length >= MAX_CONTRATOS ? 'destructive' : 'secondary'}
                  className="text-xs px-1.5 py-0"
                >
                  {contratoIds.length} / {MAX_CONTRATOS}
                </Badge>
              </div>

              {contratosLoading ? (
                <div className="space-y-1.5">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-8 w-full" />)}
                </div>
              ) : contratos.length === 0 ? (
                <p className="text-xs text-muted-foreground px-1 py-3 text-center border rounded-lg bg-muted/30">
                  Nenhum contrato acessível neste cluster.
                </p>
              ) : (
                <ScrollArea className="max-h-52 rounded-lg border border-border bg-muted/20">
                  <div className="p-2 space-y-0.5">
                    {contratos.map(c => {
                      const checked = contratoIds.includes(c.id);
                      const disabled = !checked && contratoIds.length >= MAX_CONTRATOS;
                      return (
                        <label
                          key={c.id}
                          className={`flex items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors cursor-pointer ${
                            checked ? 'bg-primary/8' : disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-muted/60'
                          }`}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => !disabled && toggleContrato(c.id)}
                            disabled={disabled}
                            className="shrink-0"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium truncate">{c.nome}</p>
                            <p className="text-xs text-muted-foreground">{c.numero_contrato}</p>
                          </div>
                          {c.status !== 'ativo' && (
                            <Badge variant="outline" className="text-xs shrink-0">{c.status}</Badge>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}

              {contratoIds.length >= MAX_CONTRATOS && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Limite de {MAX_CONTRATOS} contratos atingido.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
